/**
 * Shared modal wrapper for AI generation dialogs.
 * Provides: backdrop, dialog container, header, scrollable body, and
 * step-dependent footer buttons (with auth/balance gating).
 */

import { useTranslation } from 'react-i18next';
import { useAccountStore } from '@/store/accountStore.ts';
import { canAfford } from '@/ai/tokenCosts.ts';
import { capabilities } from '@/capabilities.ts';
import { interceptEmbeddedSignIn } from '@/components/AiWorkspace/embeddedAuthGate.ts';
import { trackEvent } from '@/telemetry/trackEvent';
import type { AiDialogStep } from '@/hooks/useAiDialogFlow.ts';
import type { ReactNode } from 'react';
import styles from './AiDialog.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  dialogWidth?: number;
  wide?: boolean;
  step: AiDialogStep;
  children: ReactNode;

  // Options step footer
  totalTokenCost: number;
  generateLabel: string;
  generateDisabled: boolean;
  onGenerate: () => void;
  onSettingsDone: () => void;
  actionVerb: string;           // "Enhance" or "Generate" — for "Sign In to {verb}"

  // Generating step footer
  estTotalSeconds: number;
  elapsedSeconds: number;
  onCancel: () => void;

  // Review step footer
  acceptedCount: number;
  applyNoun: string;            // "Enhancement" or "Video"
  onApply: () => void;

  // Optional load-from-history button in header
  onLoadFromHistory?: () => void;
}

export function AiDialogShell({
  open, onClose, title, dialogWidth, wide, step, children,
  totalTokenCost, generateLabel, generateDisabled, onGenerate, onSettingsDone, actionVerb,
  estTotalSeconds, elapsedSeconds, onCancel,
  acceptedCount, applyNoun, onApply,
  onLoadFromHistory,
}: Props) {
  const { t } = useTranslation('aiWorkspace');
  const accountUser = useAccountStore(s => s.accountUser);
  const accountBalance = useAccountStore(s => s.accountBalance);
  const setAuthDialogOpen = useAccountStore(s => s.setAuthDialogOpen);
  const setTokenPurchaseDialogOpen = useAccountStore(s => s.setTokenPurchaseDialogOpen);
  const standaloneAuthMode = useAccountStore(s => s.standaloneAuthMode);

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={step === 'generating' ? undefined : onClose}>
      <div
        className={styles.dialog}
        style={{ width: dialogWidth ?? (wide ? 880 : undefined) }}
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.accentBar} />
        <div className={styles.header}>
          <span className={styles.title}>{title}</span>
          {onLoadFromHistory && step === 'options' && (
            <button
              className={styles.loadHistoryBtn}
              onClick={onLoadFromHistory}
              title={t('loadFromHistoryTooltip')}
            >
              <svg width="14" height="14" viewBox="-1 -1 26 26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v5h5" />
                <path d="M3 8a9 9 0 1 1 1.83 5.5" />
                <polyline points="12 7 12 12 15.5 14" />
              </svg>
            </button>
          )}
          {step === 'generating' && (
            <span className={styles.headerCountdown}>
              {(() => {
                const remaining = Math.max(0, estTotalSeconds - elapsedSeconds);
                if (remaining > 0) return t('remainingSeconds', { seconds: remaining });
                return t('finishingUp');
              })()}
            </span>
          )}
          <button
            className={styles.closeBtn}
            onClick={onClose}
            title={t('close')}
            style={step === 'generating' ? { visibility: 'hidden' } : undefined}
          >
            &#10005;
          </button>
        </div>

        <div className={styles.body}>
          {children}
        </div>

        <div className={styles.footer}>
          {step === 'options' && (() => {
            const insufficient = accountBalance && !accountBalance.isBeta &&
              !canAfford(accountBalance, totalTokenCost);
            const needsAuth = !accountUser;
            return (
              <>
                <button className={styles.cancelBtn} onClick={onClose}>{t('cancel')}</button>
                <button className={styles.secondaryBtn} onClick={onSettingsDone}>{t('done')}</button>
                {needsAuth && (!capabilities.bridge || standaloneAuthMode) ? (
                  <button
                    className={styles.primaryBtn}
                    onClick={() => { onClose(); setAuthDialogOpen(true); }}
                  >
                    {t('signInToAction', { verb: actionVerb })}
                  </button>
                ) : (
                  <button
                    className={styles.primaryBtn}
                    onClick={async () => {
                      if (interceptEmbeddedSignIn(needsAuth)) return;
                      if (insufficient) {
                        const store = useAccountStore.getState();
                        if (!store.welcomeDepletionDismissedThisSession) {
                          const hasPurchased = await store.checkHasEverPurchased();
                          if (!hasPurchased) {
                            onClose();
                            store.setWelcomeDepletionOpen(true);
                            return;
                          }
                        }
                        onClose();
                        trackEvent('depletion_shown', { context: 'ai_panel' });
                        setTokenPurchaseDialogOpen(true);
                        return;
                      }
                      onGenerate();
                    }}
                    disabled={generateDisabled}
                  >
                    {generateLabel}
                  </button>
                )}
              </>
            );
          })()}
          {step === 'generating' && (
            <button className={styles.cancelBtnCentered} onClick={onCancel}>{t('cancel')}</button>
          )}
          {step === 'review' && (
            acceptedCount > 0 ? (
              <>
                <button className={styles.secondaryBtn} onClick={onClose}>{t('cancel')}</button>
                <button className={styles.primaryBtn} onClick={onApply}>
                  {t('applyCountNoun', { count: acceptedCount, noun: applyNoun, plural: acceptedCount !== 1 ? 's' : '' })}
                </button>
              </>
            ) : (
              <button className={styles.primaryBtn} onClick={onClose}>{t('close')}</button>
            )
          )}
          {step === 'done' && (
            <button className={styles.primaryBtn} onClick={onClose}>{t('close')}</button>
          )}
        </div>
      </div>
    </div>
  );
}
